import { useSelector } from 'react-redux';
import { entries, isArray, keyBy, keys, values } from 'lodash';
import { Text, Box, Flex, Tag, Card } from '@chakra-ui/react';
import { TState } from '../../../redux/store';
import { allIcons, NormalIcon } from '../../utils/Icon';
import { useGetSelect } from '../../../redux/actions';
import { TColumnData } from '../../tabs/type';
import { isEmpty } from '../../../helpers';

type TDynamicFilters = {
  Form: JSX.Element[];
  formData: Record<string, any>;
  setFormData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  sizes?: number[];
  show?: boolean;
  inputs?: TColumnData[];
};

const CUSTOM_FILTERS: Record<string, string> = {
  confirmed: 'Confirmé',
};

export const useTagFilter = () => {
  const theme = useSelector((state: TState) => state.theme);
  const getInputByName = useGetSelect();

  return (
    formData: Record<string, any>,
    handleDelete?: any,
    inputsByName?: Record<string, TColumnData>,
  ) =>
    entries(formData).map(([key, value]) => {
      if (isEmpty(value)) return;

      const formatedKey = key.replace(/_street|_city|_postal|_dept/gi, '');
      const input = getInputByName(formatedKey) || inputsByName?.[key];

      const isStreet = /_street$/gi.test(key);
      const isCity = /_city$/gi.test(key);
      const isPostal = /_postal$/gi.test(key);
      const isDept = /_dept$/gi.test(key);

      const suffixLabel = isStreet
        ? 'rue'
        : isCity
        ? 'ville'
        : isPostal
        ? 'code postal'
        : isDept
        ? 'département'
        : '';

      const label = `${
        (input?.label || CUSTOM_FILTERS[formatedKey]) ?? ''
      } ${suffixLabel}`;

      return isArray(value) ? (
        value.map((v) => (
          <Tag
            display='flex'
            flexDirection='row'
            bgGradient={`linear(to-br, ${theme.dark}, blue.300)`}
            color={theme.none}
            pr={1}
          >
            <Text>
              {label ?? key} :{' '}
              {input?.options?.find((o) => o.value === v)?.label}
            </Text>
            {handleDelete && (
              <NormalIcon
                as={allIcons.HiMiniXMark}
                boxSize={5}
                onClick={() => handleDelete(key, v)}
                color='white'
              />
            )}
          </Tag>
        ))
      ) : (
        <Tag
          display='flex'
          flexDirection='row'
          bgGradient={`linear(to-br, ${theme.dark}, blue.300)`}
          color={theme.none}
          pr={1}
        >
          <Text>
            {label ?? key} :{' '}
            {value === false ? 'Non' : value === true ? 'Oui' : value}
          </Text>
          {handleDelete && (
            <NormalIcon
              as={allIcons.HiMiniXMark}
              boxSize={5}
              onClick={() => handleDelete(key)}
              color='white'
            />
          )}
        </Tag>
      );
    });
};

const DynamicFilters = ({
  Form,
  formData,
  setFormData,
  sizes: customSizes,
  show,
  inputs,
}: TDynamicFilters) => {
  const filterActive = useSelector((state: TState) => state.filterActive);
  const modalType = useSelector((state: TState) => state.modalType);
  const theme = useSelector((state: TState) => state.theme);

  const tagFilter = useTagFilter();

  const handleDelete = (k: string, v?: any) => {
    setFormData((fd) => {
      const newFd = { ...fd };

      if (!v) {
        delete newFd[k];
      } else {
        newFd[k] = newFd[k].filter((val: any) => val !== v);
        if (!newFd[k].length) {
          delete newFd[k];
        }
      }

      return newFd;
    });
  };

  const sizes = customSizes || [1, 2, 3, 4, 5, 6];
  const display = show || filterActive;
  const inputsByName = inputs && keyBy(inputs, 'name_id');

  const filters = entries(formData).reduce(
    (prev, [k, v]) => (isEmpty(v) ? prev : { ...prev, [k]: v }),
    {},
  );

  const showFilter = /addToPlanning|updatePlanning/i.test(modalType);
  const hasFilter = keys(filters).length;

  return !display && !hasFilter ? null : (
    <Box
      className='dynamic-filters'
      zIndex={2}
      flex={1}
      flexGrow={0}
      maxH={showFilter ? '' : ['25%', '25%', '50%']}
    >
      <Card p={3} overflow={'auto'} h={'100%'} bgColor='#f9fafb'>
        {showFilter ? <Text>Filtres</Text> : null}
        {display ? (
          <>
            <Flex flexWrap='wrap' columnGap={2} alignItems='flex-end'>
              {Form.map((f) => (
                <Box
                  w={sizes.map((s) => `calc((100% - ${s - 1} * 8px) / ${s})`)}
                >
                  {f}
                </Box>
              ))}
            </Flex>
            <Tag
              onClick={() => setFormData({})}
              cursor='pointer'
              bgGradient={`linear(to-br, red.600, red.300)`}
              color={theme.none}
              mt={3}
              right={0}
              w='fit-content'
            >
              <Text>Vider les filtres</Text>
            </Tag>
          </>
        ) : (
          hasFilter && (
            <Flex
              gap={2}
              flexWrap='wrap'
              overflow='auto'
              height='100%'
              alignItems='center'
            >
              <Text fontSize={theme.fontSize.small} mr={1}>
                Filtres actifs
              </Text>
              {tagFilter(formData, handleDelete, inputsByName)}
              {!!values(formData).filter((f) => (isArray(f) ? f.length : f))
                .length && (
                <Tag
                  onClick={() => setFormData({})}
                  cursor='pointer'
                  bgGradient={`linear(to-br, red.600, red.300)`}
                  color={theme.none}
                >
                  <Text>Vider les filtres</Text>
                </Tag>
              )}
            </Flex>
          )
        )}
      </Card>
    </Box>
  );
};

export default DynamicFilters;
